import React, { useEffect, useMemo, useRef, useState } from 'react';

// Images
import logo from '../assets/image/logo.png';

// SVG Icons
import {
  BackpackIcon,
  CulturalIcon,
  GroupTourIcon,
  HistoricalIcon,
  HoneymoonIcon,
  LuxuryIcon,
} from '../components/SvgIcons';

const ExploreSidebar = ({ isOpen, onClose, promoData = null, logoUrl = logo }) => {
  const fallbackTourCategories = [
    { name: 'Adventure Tours', icon: BackpackIcon },
    { name: 'Cultural Tours', icon: CulturalIcon },
    { name: 'Group Tour', icon: GroupTourIcon },
    { name: 'Historical Tours', icon: HistoricalIcon },
    { name: 'Honeymoon Tour', icon: HoneymoonIcon },
    { name: 'Luxury Tour', icon: LuxuryIcon },
  ];

  const fallbackDestinations = [
    {
      name: 'Australia',
      image:
        'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      tours: 5,
      color: 'green',
    },
    {
      name: 'Sweden',
      image:
        'https://images.unsplash.com/photo-1550340499-a6c60fc8287c?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      tours: 5,
      color: 'blue',
    },
    {
      name: 'Japan',
      image:
        'https://images.unsplash.com/photo-1549692520-acc6669e2f0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      tours: 5,
      color: 'orange',
    },
    {
      name: 'France',
      image:
        'https://images.unsplash.com/photo-1508057198894-247b23fe5ade?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      tours: 5,
      color: 'purple',
    },
    {
      name: 'Turkey',
      image:
        'https://images.unsplash.com/photo-1544989164-31dc3c645987?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
      tours: 5,
      color: 'red',
    },
  ];

  const tourCategories = (promoData?.tour_categories?.items || []).length
    ? promoData.tour_categories.items.map((item, index) => ({
        name: item.name,
        count: item.tour_count || 0,
        icon: [BackpackIcon, CulturalIcon, GroupTourIcon, HistoricalIcon, HoneymoonIcon, LuxuryIcon][index % 6],
      }))
    : fallbackTourCategories;

  const destinations = (promoData?.destinations?.items || []).length
    ? promoData.destinations.items.map((item, index) => ({
        name: item.name,
        image: item.image_url || 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80',
        tours: item.tour_count || 1,
        color: ['green', 'blue', 'orange', 'purple', 'red'][index % 5],
      }))
    : fallbackDestinations;

  const slidesPerView = 2; // show two destination cards
  const [currentIndex, setCurrentIndex] = useState(0);
  const autoPlayMs = 3500;
  const intervalRef = useRef(null);

  const maxIndex = useMemo(() => {
    if (destinations.length <= slidesPerView) return 0;
    return destinations.length - slidesPerView;
  }, [destinations.length]);

  const next = () => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const prev = () => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  useEffect(() => {
    if (!isOpen) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(next, autoPlayMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxIndex, isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div className="sidebar-overlay" onClick={onClose}></div>

      {/* Sidebar */}
      <div className="sidebar gridSidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <span className="logo-icon">
              <img src={logoUrl} alt="TripRex" className="brand-logo" />
            </span>
          </div>
          <button className="close-btn" onClick={onClose} aria-label="Close">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className="sidebar-content">
          {/* Tour Type Section */}
          <h3 className="section-title">Tour Categories</h3>
          <div className="row tour-margin">
            {tourCategories.map((category, index) => {
              const Icon = category.icon;
              return (
                <div key={index} className="col-sm-4 col-6 mb-3">
                  <div className="tour-type-card text-center">
                    <div className="tour-icon" aria-hidden="true">
                      <Icon size={28} className="icon-svg" />
                    </div>
                    <span className="tour-name">{category.name}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Destinations Section */}
          <div className="sidebar-section">
            <h3 className="section-title">{promoData?.destinations?.heading || 'Our Destinations'}</h3>

            {/* Slider */}
            <div className="destinations-grid" style={{ overflow: 'hidden' }}>
              <div
                className="destinations-track"
                style={{
                  display: 'flex',
                  gap: '20px',
                  width: `${(destinations.length / slidesPerView) * 100}%`,
                  transform: `translateX(-${(100 / destinations.length) * currentIndex}%)`,
                  transition: 'transform 600ms ease',
                }}
              >
                {destinations.map((destination, index) => (
                  <div
                    key={index}
                    className="destination-card"
                    style={{ flex: `0 0 calc(${100 / slidesPerView}% - 10px)` }}
                  >
                    <div className="destination-image">
                      <img src={destination.image} alt={destination.name} loading="lazy" />
                      <div className={`tour-badge ${destination.color}`}>
                        {destination.tours} Tour
                      </div>
                    </div>
                    <div className="destination-info">
                      <span className="travel-to">Travel To</span>
                      <h4 className="destination-name">{destination.name}</h4>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Controls */}
            <div className="destinations-nav">
              <button className="nav-arrow left" onClick={prev} aria-label="Previous">
                 <i className="bi bi-arrow-left"></i>
              </button>
              <button className="view-all-btn">View All</button>
              <button className="nav-arrow right" onClick={next} aria-label="Next">
                <i className="bi bi-arrow-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExploreSidebar;
